/**
 * The app's QueryClient and its defaults.
 *
 * * Queries: no retries on 4xx; up to 2 retries on network errors / 5xx; a busy server (503
 *   `busy` / `db_busy`) is retried up to 5 times after its `Retry-After`. Mutations only retry
 *   busy replies (the server rejected them before doing anything).
 * * Mutations: failures show an error toast automatically. Pass
 *   `meta: { errorToast: false }` when the UI shows the error itself (forms), or
 *   `meta: { errorTitle: 'Could not save class' }` to customise the toast title.
 */
import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from '@/components/toast';
import { busyRetryDelayMs, isApiError, isBusyError } from './client';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** Show a toast when the mutation fails (default `true`). */
      errorToast?: boolean;
      /** Title of that toast (default "Something went wrong"). */
      errorTitle?: string;
    };
  }
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  // a busy server (503 busy / db_busy) asks to come back later: a few more patient tries
  if (isBusyError(error)) return failureCount < 5;
  if (failureCount >= 2) return false;
  if (!isApiError(error)) return false;
  return error.status === 0 || error.status >= 500;
}

/** TanStack's default back-off, or the server's `Retry-After` for busy replies. */
function retryDelay(failureCount: number, error: unknown): number {
  if (isBusyError(error)) return busyRetryDelayMs(error, failureCount + 1);
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.errorToast === false) return;
        toast.error(mutation.meta?.errorTitle ?? 'Something went wrong', error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        retry: shouldRetry,
        retryDelay,
      },
      // a busy server rejected the request before doing anything: retrying is safe
      mutations: {
        retry: (count, error) => isBusyError(error) && count < 3,
        retryDelay,
      },
    },
  });
}
